import React, { useState, useEffect } from 'react';

const ResultList = () => {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedExam, setSelectedExam] = useState('');

  useEffect(() => {
    // Mock data for results
    // In a real application, this would come from an API call
    const mockResults = [
      {
        id: 1,
        studentName: 'John Smith',
        rollNumber: '10A001',
        className: 'Grade 10',
        section: 'A',
        examName: 'Mid Term Exam',
        subject: 'Mathematics',
        marks: 85,
        maxMarks: 100,
        percentage: 85,
        grade: 'A',
        publishDate: '2023-11-15'
      },
      {
        id: 2,
        studentName: 'Emma Johnson',
        rollNumber: '10A002',
        className: 'Grade 10',
        section: 'A',
        examName: 'Mid Term Exam',
        subject: 'Mathematics',
        marks: 92,
        maxMarks: 100,
        percentage: 92,
        grade: 'A+',
        publishDate: '2023-11-15'
      },
      {
        id: 3,
        studentName: 'Michael Brown',
        rollNumber: '10A003',
        className: 'Grade 10',
        section: 'A',
        examName: 'Mid Term Exam',
        subject: 'Science',
        marks: 78,
        maxMarks: 100,
        percentage: 78,
        grade: 'B+',
        publishDate: '2023-11-16'
      },
      {
        id: 4,
        studentName: 'Sarah Davis',
        rollNumber: '10B001',
        className: 'Grade 10',
        section: 'B',
        examName: 'Unit Test',
        subject: 'English',
        marks: 88,
        maxMarks: 100,
        percentage: 88,
        grade: 'A',
        publishDate: '2023-10-20'
      },
      {
        id: 5,
        studentName: 'David Wilson',
        rollNumber: '10B002',
        className: 'Grade 10',
        section: 'B',
        examName: 'Quiz',
        subject: 'History',
        marks: 45,
        maxMarks: 50,
        percentage: 90,
        grade: 'A',
        publishDate: '2023-10-25'
      }
    ];

    setTimeout(() => {
      setResults(mockResults);
      setLoading(false);
    }, 1000); // Simulate API call delay
  }, []);

  const filteredResults = results.filter(result => {
    return (!selectedClass || result.className === selectedClass) &&
           (!selectedExam || result.examName === selectedExam);
  });

  const uniqueClasses = [...new Set(results.map(result => result.className))];
  const uniqueExams = [...new Set(results.map(result => result.examName))];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8">
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-gray-800">Results Management</h1>
          <button className="bg-primary text-white px-4 py-2 rounded hover:bg-blue-700 transition flex items-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
            </svg>
            Upload Result
          </button>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div>
              <label className="block text-gray-700 mb-2">Filter by Class</label>
              <select
                value={selectedClass}
                onChange={(e) => setSelectedClass(e.target.value)}
                className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="">All Classes</option>
                {uniqueClasses.map(className => (
                  <option key={className} value={className}>{className}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-gray-700 mb-2">Filter by Exam</label>
              <select
                value={selectedExam}
                onChange={(e) => setSelectedExam(e.target.value)}
                className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="">All Exams</option>
                {uniqueExams.map(examName => (
                  <option key={examName} value={examName}>{examName}</option>
                ))}
              </select>
            </div>
            
            <div className="flex items-end">
              <button className="w-full bg-gray-200 text-gray-800 py-2 px-4 rounded hover:bg-gray-300 transition">
                Export
              </button>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Roll Number</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Student Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Class</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Exam</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subject</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Marks</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">%</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Grade</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredResults.map((result) => (
                  <tr key={result.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{result.rollNumber}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{result.studentName}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{result.className}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{result.examName}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{result.subject}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{result.marks}/{result.maxMarks}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{result.percentage}%</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        result.grade === 'A+' ? 'bg-purple-100 text-purple-800' :
                        result.grade === 'A' ? 'bg-green-100 text-green-800' :
                        result.grade === 'B+' ? 'bg-blue-100 text-blue-800' :
                        result.grade === 'B' ? 'bg-yellow-100 text-yellow-800' :
                        result.grade === 'C' ? 'bg-orange-100 text-orange-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {result.grade}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{result.publishDate}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button className="text-primary hover:text-blue-700 mr-4">Edit</button>
                      <button className="text-red-600 hover:text-red-800">Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Overall Statistics</h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span>Total Results</span>
                <span className="font-medium">{results.length}</span>
              </div>
              <div className="flex justify-between">
                <span>Avg. Percentage</span>
                <span className="font-medium text-green-600">
                  {results.length > 0 ? (results.reduce((sum, r) => sum + r.percentage, 0) / results.length).toFixed(1) : 0}%
                </span>
              </div>
              <div className="flex justify-between">
                <span>Highest Score</span>
                <span className="font-medium text-green-600">
                  {results.length > 0 ? Math.max(...results.map(r => r.percentage)) : 0}%
                </span>
              </div>
              <div className="flex justify-between">
                <span>Lowest Score</span>
                <span className="font-medium text-red-600">
                  {results.length > 0 ? Math.min(...results.map(r => r.percentage)) : 0}%
                </span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Grade Distribution</h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span>A+ Grade</span>
                <span className="font-medium text-purple-600">
                  {results.filter(r => r.grade === 'A+').length}
                </span>
              </div>
              <div className="flex justify-between">
                <span>A Grade</span>
                <span className="font-medium text-green-600">
                  {results.filter(r => r.grade === 'A').length}
                </span>
              </div>
              <div className="flex justify-between">
                <span>B+ Grade</span>
                <span className="font-medium text-blue-600">
                  {results.filter(r => r.grade === 'B+').length}
                </span>
              </div>
              <div className="flex justify-between">
                <span>F Grade</span>
                <span className="font-medium text-red-600">
                  {results.filter(r => r.grade === 'F').length}
                </span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Quick Actions</h3>
            <div className="space-y-3">
              <button className="w-full bg-primary text-white py-2 px-4 rounded hover:bg-blue-700 transition text-sm">
                Publish Results
              </button>
              <button className="w-full bg-green-600 text-white py-2 px-4 rounded hover:bg-green-700 transition text-sm">
                Generate Report
              </button>
              <button className="w-full bg-purple-600 text-white py-2 px-4 rounded hover:bg-purple-700 transition text-sm">
                Send Notifications
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResultList;